import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { useAuth } from './AuthProvider';
import {
  autoAcceptPendingInvitations,
  listMyMemberships,
  listMyOrganizations,
  listStoresForOrg,
  userHasOrgWideAccess,
} from '@pc/db';

const LS_ORG = 'pc.activeOrgId';
const LS_STORE = 'pc.activeStoreId';
const LS_MODE = 'pc.activeMode';

/**
 * Operator workspace modes.
 *
 *   * `'store'` -- legacy default. Both `activeOrgId` and `activeStoreId`
 *     are set; the full per-store shell (`AppShell`) renders.
 *   * `'org'`   -- new "Enter organization" mode. `activeOrgId` is set,
 *     `activeStoreId` MAY be null, and the slim `OrgAppShell` renders
 *     combined analytics + cross-store pivots that deep-link into a
 *     store before letting the operator edit.
 */
export type ActiveMode = 'store' | 'org';

interface ActiveContextValue {
  activeOrgId: string | null;
  activeStoreId: string | null;
  mode: ActiveMode;
  ready: boolean;
  /** All orgs the current user has membership in. */
  availableOrgIds: string[];
  /**
   * Orgs the current user has ORG-WIDE access to (org-scoped membership or
   * owner/admin). Mirrors `core.user_has_org_wide_access`. Used to gate
   * "Enter organization" mode — a store-scoped member must never sit in the
   * org shell (the aggregate RPCs 42501 for them).
   */
  orgWideOrgIds: string[];
  /** True when the user has org-wide access to the ACTIVE org. */
  hasOrgWideAccess: boolean;
  /** Stores within the active org. */
  availableStoreIds: string[];
  /** Pick an org AND auto-pick the only store (legacy store-mode entry). */
  setActiveOrg(orgId: string): Promise<void>;
  /** Pick a store; flips mode back to 'store'. */
  setActiveStore(storeId: string): void;
  /**
   * Enter org mode for the given org. Sets `activeOrgId`, clears
   * `activeStoreId`, sets `mode='org'`. Used by the "Enter organization"
   * row on the OrgStorePicker.
   */
  setActiveOrgMode(orgId: string): Promise<void>;
  clear(): void;
}

const Ctx = createContext<ActiveContextValue | null>(null);

function readMode(): ActiveMode {
  const raw = typeof window === 'undefined' ? null : localStorage.getItem(LS_MODE);
  return raw === 'org' ? 'org' : 'store';
}

/** Synchronous LS read so the shell can paint on reload without a second boot flash. */
function readCachedWorkspace(): {
  orgId: string | null;
  storeId: string | null;
  mode: ActiveMode;
  ready: boolean;
} {
  if (typeof window === 'undefined') {
    return { orgId: null, storeId: null, mode: 'store', ready: false };
  }
  const orgId = localStorage.getItem(LS_ORG);
  const storeId = localStorage.getItem(LS_STORE);
  const mode = readMode();
  if (!orgId) return { orgId: null, storeId: null, mode: 'store', ready: true };
  // Org mode must wait for membership hydrate: `orgWideOrgIds` starts empty,
  // so marking ready here made `hasOrgWideAccess` false on the first paint and
  // `RequireOrgContext` bounced owners back to `/choose-context`.
  if (mode === 'org') return { orgId, storeId: null, mode: 'org', ready: false };
  if (storeId) return { orgId, storeId, mode: 'store', ready: true };
  return { orgId, storeId: null, mode: 'store', ready: false };
}

export function ActiveContextProvider({ children }: { children: ReactNode }) {
  const { session, loading: authLoading } = useAuth();
  const cached = readCachedWorkspace();
  const [activeOrgId, setOrgIdState] = useState<string | null>(() => cached.orgId);
  const [activeStoreId, setStoreIdState] = useState<string | null>(() => cached.storeId);
  const [mode, setModeState] = useState<ActiveMode>(() => cached.mode);
  const [availableOrgIds, setAvailableOrgIds] = useState<string[]>([]);
  const [orgWideOrgIds, setOrgWideOrgIds] = useState<string[]>([]);
  const [availableStoreIds, setAvailableStoreIds] = useState<string[]>([]);
  const [ready, setReady] = useState(() => cached.ready);

  // Tracks the user_id we've already hydrated for, so duplicate auth events
  // -- INITIAL_SESSION + SIGNED_IN both fire on first sign-in, and
  // TOKEN_REFRESHED fires every ~50 minutes producing a NEW session object
  // reference -- don't kick off a redundant hydrate. The redundant hydrates
  // were the root cause of the "click Enter 10 times" picker bug: an
  // in-flight hydrate would race the user's `setActiveOrg`/`setActiveStore`
  // and overwrite their selection with whatever it computed from
  // localStorage at the moment its async work resolved.
  const hydratedForUserId = useRef<string | null>(null);

  const userId = session?.user?.id ?? null;

  // Hydrate from localStorage and verify against current memberships. Runs
  // ONCE per signed-in user, never on token refresh, never twice for the
  // same user.
  useEffect(() => {
    if (authLoading) return;
    if (!userId) {
      hydratedForUserId.current = null;
      setReady(true);
      return;
    }
    if (hydratedForUserId.current === userId) {
      // Already hydrated this user. Don't re-fire on token refresh.
      return;
    }
    hydratedForUserId.current = userId;

    let alive = true;
    (async () => {
      try {
        // Drain any pending invitations for this user's email BEFORE we
        // fetch their orgs. Covers users who signed in through a path
        // that didn't explicitly accept invitations.
        try {
          await autoAcceptPendingInvitations();
        } catch (e) {
          console.warn('[ActiveContext] auto-accept pending invitations failed:', e);
        }

        const [orgs, memberships] = await Promise.all([listMyOrganizations(), listMyMemberships()]);
        const orgIds = orgs.map((o) => o.id);
        if (!alive) return;
        setAvailableOrgIds(orgIds);
        // Compute (and remember) which of those orgs the user has ORG-WIDE
        // access to, so the route guard + the reactive downgrade below can
        // keep a store-scoped member out of org mode without another fetch.
        setOrgWideOrgIds(orgIds.filter((id) => userHasOrgWideAccess(memberships, id)));

        let savedOrg = localStorage.getItem(LS_ORG);
        if (savedOrg && !orgIds.includes(savedOrg)) savedOrg = null;
        const initialOrg = savedOrg ?? (orgIds.length === 1 ? orgIds[0]! : null);
        setOrgIdState(initialOrg);

        const savedMode = readMode();

        if (initialOrg) {
          const stores = await listStoresForOrg(initialOrg);
          if (!alive) return;
          const storeIds = stores.map((s) => s.id);

          let savedStore = localStorage.getItem(LS_STORE);
          if (savedStore && !storeIds.includes(savedStore)) savedStore = null;

          // Auto-pick if there's exactly one option and the user has org-wide
          // (or specific) access to it.
          const allowedFromMembership = memberships
            .filter((m) => m.org_id === initialOrg)
            .map((m) => m.store_id);
          const orgWide = allowedFromMembership.some((s) => s === null);
          const allowedStoreIds = orgWide
            ? storeIds
            : storeIds.filter((id) => allowedFromMembership.includes(id));
          setAvailableStoreIds(allowedStoreIds);

          if (savedMode === 'org' && orgWide) {
            // Org mode: keep activeStoreId null even if an auto-pick is possible.
            // ONLY honored for org-wide members. A store-scoped member (no
            // store_id=null membership) must never sit in org mode -- the
            // org-mode aggregate RPCs are server-gated on
            // core.user_has_org_wide_access and would 42501 anyway, so we
            // downgrade to store mode here to avoid a broken shell.
            setStoreIdState(null);
            localStorage.removeItem(LS_STORE);
            setModeState('org');
            localStorage.setItem(LS_MODE, 'org');
          } else {
            const initialStore =
              savedStore ?? (allowedStoreIds.length === 1 ? allowedStoreIds[0]! : null);
            setStoreIdState(initialStore);
            if (initialStore) localStorage.setItem(LS_STORE, initialStore);
            setModeState('store');
            // Normalize the LS key so older clients without LS_MODE upgrade.
            localStorage.setItem(LS_MODE, 'store');
          }
        } else {
          setAvailableStoreIds([]);
          setModeState('store');
          localStorage.removeItem(LS_MODE);
        }
      } finally {
        if (alive) setReady(true);
      }
    })();
    return () => {
      alive = false;
    };
  }, [authLoading, userId]);

  const setActiveOrg = useCallback(
    async (orgId: string) => {
      // Same-org no-op. Without this short-circuit the picker's onEnter
      // ends up making a redundant listStoresForOrg round-trip AND
      // transiently sets activeStoreId to null before re-resolving it.
      if (orgId === activeOrgId && mode === 'store') {
        localStorage.setItem(LS_ORG, orgId);
        return;
      }
      setOrgIdState(orgId);
      localStorage.setItem(LS_ORG, orgId);
      setModeState('store');
      localStorage.setItem(LS_MODE, 'store');
      const stores = await listStoresForOrg(orgId);
      const storeIds = stores.map((s) => s.id);
      setAvailableStoreIds(storeIds);
      // Decide the new store value once, then commit.
      const nextStore = storeIds.length === 1 ? storeIds[0]! : null;
      setStoreIdState(nextStore);
      if (nextStore) {
        localStorage.setItem(LS_STORE, nextStore);
      } else {
        localStorage.removeItem(LS_STORE);
      }
    },
    [activeOrgId, mode],
  );

  const setActiveStore = useCallback((storeId: string) => {
    setStoreIdState(storeId);
    localStorage.setItem(LS_STORE, storeId);
    setModeState('store');
    localStorage.setItem(LS_MODE, 'store');
  }, []);

  const setActiveOrgMode = useCallback(
    async (orgId: string) => {
      // Guard: never enter org mode for an org the user lacks ORG-WIDE access
      // to (the picker already hides the "Enter organization" button for them,
      // but this is the programmatic backstop). Fall back to store mode.
      if (!orgWideOrgIds.includes(orgId)) {
        await setActiveOrg(orgId);
        return;
      }
      // Enter org mode. Set org if it changed; always clear store and flip mode.
      if (orgId !== activeOrgId) {
        setOrgIdState(orgId);
        localStorage.setItem(LS_ORG, orgId);
        // Refresh availableStoreIds so the OrgAppShell footer's "Enter a store"
        // button can pre-populate the picker.
        const stores = await listStoresForOrg(orgId);
        setAvailableStoreIds(stores.map((s) => s.id));
      }
      setStoreIdState(null);
      localStorage.removeItem(LS_STORE);
      setModeState('org');
      localStorage.setItem(LS_MODE, 'org');
    },
    [activeOrgId, orgWideOrgIds, setActiveOrg],
  );

  const clear = useCallback(() => {
    setOrgIdState(null);
    setStoreIdState(null);
    setModeState('store');
    localStorage.removeItem(LS_ORG);
    localStorage.removeItem(LS_STORE);
    localStorage.removeItem(LS_MODE);
  }, []);

  // Cross-tab + in-app-browser safety net. Three real failure modes this
  // closes:
  //   1. User clicks "Switch workspace" in tab A -> `clear()` wipes
  //      LS_ORG/LS_STORE/LS_MODE. Tab B still has activeOrgId/activeStoreId
  //      in React state.
  //   2. User signs out in tab A.
  //   3. iOS in-app browsers (Spark, Gmail webview, etc.) sometimes evict
  //      localStorage between launches even while the Supabase session
  //      cookie survives.
  useEffect(() => {
    function syncFromStorage() {
      const storedOrg = localStorage.getItem(LS_ORG);
      const storedStore = localStorage.getItem(LS_STORE);
      const storedMode = readMode();
      if (
        storedOrg !== activeOrgId ||
        storedStore !== activeStoreId ||
        storedMode !== mode
      ) {
        setOrgIdState(storedOrg);
        setStoreIdState(storedStore);
        setModeState(storedMode);
      }
    }
    function onStorage(e: StorageEvent) {
      if (
        e.key === LS_ORG ||
        e.key === LS_STORE ||
        e.key === LS_MODE ||
        e.key === null
      ) {
        syncFromStorage();
      }
    }
    function onPageShow(e: PageTransitionEvent) {
      if (e.persisted) syncFromStorage();
    }
    window.addEventListener('storage', onStorage);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [activeOrgId, activeStoreId, mode]);

  const hasOrgWideAccess = useMemo(
    () => (activeOrgId ? orgWideOrgIds.includes(activeOrgId) : false),
    [activeOrgId, orgWideOrgIds],
  );

  // Reactive safety net: a store-scoped member must never sit in org mode.
  // The hydrate path above already downgrades on first load, but this also
  // catches a cross-tab mode flip, a stale `pc.activeMode='org'` adopted by
  // the storage-sync effect, or any future code path that sets org mode for
  // an org the user lacks org-wide access to. Flipping `mode` to 'store'
  // rebuilds the router (keyed on `mode` in App.tsx) out of the org shell;
  // RequireOrgContext additionally redirects to the picker for the in-flight
  // render. Guarded on `ready` so we never downgrade before memberships load.
  useEffect(() => {
    if (!ready) return;
    if (mode !== 'org') return;
    if (activeOrgId && orgWideOrgIds.includes(activeOrgId)) return;
    setModeState('store');
    localStorage.setItem(LS_MODE, 'store');
  }, [ready, mode, activeOrgId, orgWideOrgIds]);

  const value = useMemo<ActiveContextValue>(
    () => ({
      activeOrgId,
      activeStoreId,
      mode,
      ready,
      availableOrgIds,
      orgWideOrgIds,
      hasOrgWideAccess,
      availableStoreIds,
      setActiveOrg,
      setActiveStore,
      setActiveOrgMode,
      clear,
    }),
    [
      activeOrgId,
      activeStoreId,
      mode,
      ready,
      availableOrgIds,
      orgWideOrgIds,
      hasOrgWideAccess,
      availableStoreIds,
      setActiveOrg,
      setActiveStore,
      setActiveOrgMode,
      clear,
    ],
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useActiveContext(): ActiveContextValue {
  const v = useContext(Ctx);
  if (!v) throw new Error('useActiveContext must be used inside <ActiveContextProvider>.');
  return v;
}
